import InfoIcon from "@mui/icons-material/Info";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  DEFAULT_DRAG_HANDLE_SELECTOR,
  NO_DRAG_SELECTOR,
} from "../../../../app/constants";
import { useMemoizedDraggablePaper } from "../dialogs/useMemoizedDraggablePaper";
import OverlaySpinner from "../overlaySpinner/OverlaySpinner";
import { CustomButton } from "../customButton/CustomButton";

interface Props {
  isOpen: boolean;
  dialogTitle: string;
  dialogInfo?: string;
  onCancelDialog: () => void;
  onFinishDialog?: () => void;
  steps: { label?: string; component: JSX.Element; disableNext?: boolean }[];
  cancelLabel: string;
  finishLabel: string;
  hasSearchField?: boolean;
  updateSearchString?: (searchString: string) => void;
  isLoading?: boolean;
  validateSearchString?: (searchString: string) => boolean;
  searchStringInvalidText?: string;
  draggable?: boolean;
  dragHandleSelector?: string;
}

export const DialogBox = ({
  isOpen,
  dialogTitle,
  dialogInfo,
  onCancelDialog,
  onFinishDialog,
  steps,
  cancelLabel,
  finishLabel,
  hasSearchField,
  updateSearchString,
  isLoading,
  validateSearchString,
  searchStringInvalidText,
  draggable = true,
  dragHandleSelector = DEFAULT_DRAG_HANDLE_SELECTOR,
}: Props) => {
  const { t } = useTranslation("common");
  const [activeStep, setActiveStep] = useState(0);

  const [searchValue, setSearchValue] = useState("");
  const [hasError, setHasError] = useState(false);

  const MemoizedDraggablePaper = useMemoizedDraggablePaper(
    draggable,
    dragHandleSelector,
  );

  const cursor = draggable ? "grab" : "auto";

  const handleNext = () => {
    if (activeStep !== steps.length - 1) {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    } else {
      onFinishDialog && onFinishDialog();
    }
  };

  const handleBack = () => {
    if (activeStep === 0) {
      onCancelDialog();
    } else {
      setActiveStep((prevActiveStep) => prevActiveStep - 1);
    }
  };

  useEffect(() => {
    if (!searchValue || !validateSearchString) return;
    const isValid = validateSearchString(searchValue);
    setHasError(!isValid);
  }, [searchValue, validateSearchString]);

  useEffect(() => {
    if (updateSearchString && !hasError) {
      const delayDebounceFn = setTimeout(() => {
        updateSearchString(searchValue);
      }, 500);

      return () => clearTimeout(delayDebounceFn);
    }
  }, [searchValue, hasError, updateSearchString]);

  const renderButtons = () => {
    return (
      <>
        <CustomButton
          variant="text"
          onClick={handleBack}
          sx={{ borderRadius: "18px" }}
          disabled={isLoading}
        >
          {activeStep > 0 ? t("back") : cancelLabel}
        </CustomButton>
        {onFinishDialog ? (
          <CustomButton
            disabled={!!steps[activeStep].disableNext}
            variant="contained"
            onClick={handleNext}
            sx={{ borderRadius: "18px" }}
          >
            {activeStep === steps.length - 1 ? finishLabel : t("continue")}
          </CustomButton>
        ) : null}
      </>
    );
  };

  return (
    <Box sx={{ width: "800px" }}>
      <Dialog
        open={isOpen}
        fullWidth={true}
        PaperProps={{ style: { borderRadius: "12px" } }}
        PaperComponent={MemoizedDraggablePaper}
      >
        <OverlaySpinner isLoading={!!isLoading} />
        <Grid
          container
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            backgroundColor: "#EAEAF1",
          }}
        >
          {steps.length === 1 ? (
            !hasSearchField ? (
              <Grid className={dragHandleSelector} item xs={12} sx={{ cursor }}>
                <DialogTitle sx={{ padding: "15px" }}>
                  <Typography variant="header" sx={{ fontWeight: 600 }}>
                    {dialogTitle}
                  </Typography>
                </DialogTitle>
              </Grid>
            ) : (
              <Grid
                className={dragHandleSelector}
                container
                height="75px"
                sx={{ cursor }}
              >
                <Grid item xs={4}>
                  <DialogTitle sx={{ padding: "15px" }}>
                    <Typography variant="header" sx={{ fontWeight: 600 }}>
                      {dialogTitle}
                    </Typography>
                  </DialogTitle>
                </Grid>
                <Grid
                  item
                  xs={8}
                  sx={{ paddingRight: "20px", paddingTop: "10px" }}
                >
                  <TextField
                    className={NO_DRAG_SELECTOR}
                    helperText={hasError ? searchStringInvalidText : ""}
                    error={hasError}
                    InputProps={{
                      startAdornment: <SearchIcon />,
                      style: { backgroundColor: "rgba(249, 249, 250, 0.6)" },
                    }}
                    value={searchValue}
                    onChange={(event: any) =>
                      setSearchValue(event.target.value)
                    }
                    size="small"
                    fullWidth
                    FormHelperTextProps={{
                      style: { backgroundColor: "none" },
                    }}
                    inputProps={{ maxLength: 128 }}
                  />
                </Grid>
              </Grid>
            )
          ) : (
            <>
              <Grid item xs={4}>
                <DialogTitle sx={{ padding: "15px" }}>
                  <Typography variant="header" sx={{ fontWeight: 600 }}>
                    {dialogTitle}
                  </Typography>
                </DialogTitle>
              </Grid>
              <Grid item xs={8}>
                <DialogTitle hidden={steps.length === 1}>
                  <Stepper activeStep={activeStep}>
                    {steps.map((step, i) => {
                      const stepProps: { completed?: boolean } = {};
                      const labelProps: {
                        optional?: React.ReactNode;
                      } = {};
                      return (
                        <Step key={step.label || i} {...stepProps}>
                          <StepLabel {...labelProps}>{step.label}</StepLabel>
                        </Step>
                      );
                    })}
                  </Stepper>
                </DialogTitle>
              </Grid>
            </>
          )}
          {!!dialogInfo ? (
            <>
              <Grid item xs={1}>
                <InfoIcon sx={{ margin: "20px" }} />
              </Grid>
              <Grid item xs={11}>
                <Typography sx={{ margin: "10px 20px" }}>
                  {dialogInfo}
                </Typography>
              </Grid>
            </>
          ) : null}
        </Grid>
        <DialogContent sx={{ padding: "15px" }}>
          {steps[activeStep].component}
          <Divider sx={{ marginTop: "30px" }} />
        </DialogContent>
        <DialogActions
          className={dragHandleSelector}
          sx={{
            display: "flex",
            justifyContent: "start",
            padding: "0px 15px 15px 15px",
            cursor,
          }}
        >
          {renderButtons()}
        </DialogActions>
      </Dialog>
    </Box>
  );
};
